import type {MouseEventHandler, ReactElement} from 'react';
import {useContext} from 'react';
import type {IconSize} from '@Components/icon-v2/icon.types';
import {IconShape, IconType} from '@Components/icon-v2/icon.types';
import {Text, TextSize} from '@Components/text';
import useWindowSize from '@Hooks/useWindowSize';
import {MOBILE_MODAL_WIDTH_THRESHOLD} from '@Components/modal';
import {PANEL_HEADER_ICON_SIZE} from '@Components/panel/panel.types';
import {ModalDetails} from '@Components/modal/modal-context';
import type {IconProps} from '@Components/icon-v2';
import {Icon} from '@Components/icon-v2';
import styles from './panel-header.module.scss';
import type {PanelHeaderActionProps} from '../panel-header-action';
import {PanelHeaderActions} from '../panel-header-actions';

export enum HeaderSize {
  SMALL = 'small',
  MEDIUM = 'medium',
  LARGE = 'large',
}

export interface PanelHeaderProps {
  title?: string;
  titleClasses?: string;
  backButtonIconSize?: IconSize;
  closeButtonIconSize?: IconSize;
  centerAlignTitle?: boolean;
  forceCenterAlignTitleWithActionItems?: boolean;
  showBackButton?: boolean;
  onBackButton?: MouseEventHandler;
  backButtonClassName?: string;
  backButtonTestId?: string;
  showCloseButton?: boolean;
  onClose?: MouseEventHandler;
  actions?: PanelHeaderActionProps[];
  className?: string;
  isOpenedInModal?: boolean;
  showMobileModalHeaderBorder?: boolean;
  leftIconProps?: IconProps;
  titleTextSize?: TextSize;
  isTitleBold?: boolean;
  customTitle?: ReactElement<any>;
  closeIconClasses?: string;
  actionsClassName?: string;
  size?: HeaderSize;
  isCustomPanel?: boolean;
  customPanel?: ReactElement<any>;
  'data-testid'?: string;
}

export function PanelHeader({
  centerAlignTitle = false,
  titleClasses = '',
  showBackButton = false,
  showCloseButton = true,
  onClose = (): void => {},
  onBackButton = (): void => {},
  title = '',
  actions = [],
  className = '',
  backButtonClassName = 'spacing-m-r-2',
  isOpenedInModal = false,
  showMobileModalHeaderBorder = false,
  forceCenterAlignTitleWithActionItems = false,
  titleTextSize = TextSize.SMALL,
  isTitleBold = true,
  actionsClassName = '',
  size = HeaderSize.MEDIUM,
  isCustomPanel = false,
  ...props
}: PanelHeaderProps): ReactElement<any> {
  const modalDetails = useContext(ModalDetails);
  const {windowWidth} = useWindowSize();

  const isMobileModal = (): boolean => {
    return modalDetails?.isMobileModal ?? windowWidth < MOBILE_MODAL_WIDTH_THRESHOLD;
  };

  const getBackButtonIconSize = (): IconSize => {
    return props.backButtonIconSize ?? PANEL_HEADER_ICON_SIZE;
  };

  const getCloseButtonIconSize = (): IconSize => {
    return props.closeButtonIconSize ?? PANEL_HEADER_ICON_SIZE;
  };

  const getBackButton = (): ReactElement<any> => {
    if (!showBackButton) {
      return <div />;
    }

    return (
      <div className={`${backButtonClassName} ${isMobileModal() && isOpenedInModal ? styles.actions : ''}`}>
        <Icon icon="icon-arrow-left" shape={IconShape.SQUARE} size={getBackButtonIconSize()} onClick={onBackButton} type={IconType.GHOST} data-testid={props.backButtonTestId} />
      </div>
    );
  };

  const getTextContainer = (): ReactElement<any> => {
    let textComponent;

    if (props.customTitle) {
      textComponent = props.customTitle;
    } else {
      textComponent = (
        <Text
          size={titleTextSize}
          bold={isTitleBold}
          className={`${titleClasses} ${styles.title} ${!showBackButton && !centerAlignTitle && !forceCenterAlignTitleWithActionItems ? 'spacing-m-l-3' : ''}`}
          val={title}
        />
      );
    }

    if (props.leftIconProps) {
      return (
        <div className={`flex-h-row flex-align-items-center ${styles.textContainer}`}>
          <Icon {...props.leftIconProps} />
          {textComponent}
        </div>
      );
    }
    return textComponent;
  };

  const getHeaderSizeClass = (): string => {
    if (size === HeaderSize.SMALL) {
      return styles.smallHeader;
    }
    return styles.mediumHeader;
  };

  return (
    <div
      data-testid={props['data-testid']}
      className={`${styles.header} ${getHeaderSizeClass()} ${isOpenedInModal && isMobileModal() ? styles.mobileModalHeader : ''} ${
        isOpenedInModal && isMobileModal() && showMobileModalHeaderBorder ? styles.mobileModalHeaderWithBorder : ''
      } ${className} ${centerAlignTitle ? styles.centerAlignedTitle : ''}`}
    >
      {getBackButton()}
      {isCustomPanel && props.customPanel ? props.customPanel : getTextContainer()}
      <PanelHeaderActions
        closeButtonIconSize={getCloseButtonIconSize()}
        actions={centerAlignTitle && !forceCenterAlignTitleWithActionItems ? [] : actions}
        showCloseButton={showCloseButton}
        onClose={onClose}
        closeIconClasses={props.closeIconClasses}
        className={`${styles.headerActions} ${isMobileModal() && isOpenedInModal ? styles.actions : ''} ${actionsClassName}`}
      />
    </div>
  );
}
